import { msg } from '@gonfalon/messages/msg';
import { Tag, TagGroup, TagList, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

/** Shown in labels like "+>999%" when the magnitude is abbreviated. */
const TREND_PERCENT_ABBREV_CAP = 999999;

/**
 * When |rounded percent| is at or above this, the label uses ">"/"<" instead of the full number
 * so the chip stays short (avoids long strings like "+12345.67%").
 */
const TREND_PERCENT_ABBREV_THRESHOLD = TREND_PERCENT_ABBREV_CAP + 1;

function formatTrendLabel(trend: number): string {
  const pctRounded = parseFloat((trend * 100).toFixed(2));
  if (pctRounded >= TREND_PERCENT_ABBREV_THRESHOLD) {
    return `+>${TREND_PERCENT_ABBREV_CAP}%`;
  }
  if (pctRounded <= -TREND_PERCENT_ABBREV_THRESHOLD) {
    return `<-${TREND_PERCENT_ABBREV_CAP}%`;
  }
  return `${trend > 0 ? '+' : ''}${pctRounded}%`;
}

export type TrendChipProps = {
  /** The trend value as a ratio (e.g. 0.15 = +15%, -0.05 = -5%) */
  trend: number;
  /** Tooltip text describing the comparison period */
  tooltip?: string;
  /** Override the automatic sentiment coloring */
  sentiment?: 'success' | 'error';
};

export function TrendChip({ trend, tooltip, sentiment }: TrendChipProps) {
  const variant = sentiment ?? (trend > 0 ? 'success' : trend < 0 ? 'error' : undefined);
  const label = formatTrendLabel(trend);

  const tag = (
    <TagGroup aria-label={msg('ui.trendChip.label')}>
      <TagList>
        <Tag variant={variant}>{label}</Tag>
      </TagList>
    </TagGroup>
  );

  if (tooltip) {
    return (
      <TooltipTrigger delay={200}>
        {tag}
        <Tooltip placement="top">{tooltip}</Tooltip>
      </TooltipTrigger>
    );
  }

  return tag;
}
